if (!customElements.get('shipping-message')) {
  class ShippingMessage extends HTMLElement {
    static MIN_DAYS_FOR_SPECIFIC_DATE = 3;

    constructor() {
      super();
      this.nonWorkingDates = [];
      this.cutoffHour = 15;
      this.messageTemplate = '';
      this.countdownInterval = null;
      this.textContainer = this.querySelector('[data-text]');
      this.languageIsoCode = this.dataset.isoCodeLanguage || 'bg';
      this.countryIsoCode = this.dataset.isoCodeCountry || 'BG';
      this.timeZone = this.dataset.timezone || 'Europe/Sofia';
    }  

    get shopDate() {
      const now = new Date();
      const locale = `${this.languageIsoCode}-${this.countryIsoCode}`;

      const parts = new Intl.DateTimeFormat(locale, {
        timeZone: this.timeZone,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false
      }).formatToParts(now);

      const map = Object.fromEntries(parts.filter(part => part.type !== 'literal').map(part => [part.type, part.value]));

      return new Date(`${map.year}-${map.month}-${map.day}T${map.hour}:${map.minute}:${map.second}`);
    }

    get shopLocale() {
      return `${this.languageIsoCode}-${this.countryIsoCode}`;
    }

    connectedCallback() {
      this.initializeSettings();
      this.startCountdown();
    }

    disconnectedCallback() {
      if (this.countdownInterval) {
        clearInterval(this.countdownInterval);
      }
    }

    initializeSettings() {
      // Get settings from data attributes
      const nonWorkingDatesStr = this.dataset.nonWorkingDates || '';
      const cutoffHour = parseInt(this.dataset.cutoffHour) || 15;
      const messageTemplate = this.dataset.messageTemplate || '';

      this.nonWorkingDates = nonWorkingDatesStr ? nonWorkingDatesStr.split(',').map(date => date.trim()).filter(date => date) : [];
      this.cutoffHour = cutoffHour;
      this.messageTemplate = messageTemplate;
    }

    startCountdown() {
      this.updateMessage();
      this.countdownInterval = setInterval(() => {
        this.updateMessage();
      }, 1000); // Update every second
    }

    updateMessage() {
      const now = this.shopDate;
      const { remainingTime, targetDate } = this.getRemainingProcessingTime(now);

      if (remainingTime <= 0) {
        this.textContainer.innerHTML = this.generateMessage('0:00:00', targetDate);
        return;
      }

      const timeString = this.formatTime(remainingTime);
      this.textContainer.innerHTML = this.generateMessage(timeString, targetDate);
    }

    getRemainingProcessingTime(currentDate) {
      let targetDate = new Date(currentDate);
      
      // If it's after cutoff hour, move to next day for both target and delivery
      if (currentDate.getHours() >= this.cutoffHour) {
        targetDate.setDate(targetDate.getDate() + 1);
      }

      // Find next working day for shipping (targetDate) - store doesn't work on weekends
      targetDate = this.getNextWorkingDay(targetDate);
      
      // Set cutoff time
      targetDate.setHours(this.cutoffHour, 0, 0, 0);

      const remainingTime = Math.max(0, targetDate.getTime() - currentDate.getTime());

      return { remainingTime, targetDate };
    }

    getNextWorkingDay(date) {
      let nextDate = new Date(date);
      
      while (this.isNonWorkingDay(nextDate)) {
        nextDate.setDate(nextDate.getDate() + 1);
      }
      
      return nextDate;
    }

    isNonWorkingDay(date) {
      const dayOfWeek = date.getDay();
      const dateString = this.formatDate(date);

      // Check if it's weekend (Saturday = 6, Sunday = 0) - store doesn't work
      if (dayOfWeek === 0 || dayOfWeek === 6) {
        return true;
      }
      
      // Check if it's in the non-working dates list
      return this.nonWorkingDates.includes(dateString);
    }

    formatDate(date) {
      const day = date.getDate().toString().padStart(2, '0');
      const month = (date.getMonth() + 1).toString().padStart(2, '0');
      const year = date.getFullYear();

      return `${year}-${month}-${day}`;
    }

    formatTime(milliseconds) {
      const totalSeconds = Math.floor(milliseconds / 1000);
      const hours = Math.floor(totalSeconds / 3600);
      const minutes = Math.floor((totalSeconds % 3600) / 60);
      const seconds = totalSeconds % 60;
      
      return `${hours}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
    }

    generateMessage(timeString, targetDate) {
      const dayName = this.getWeekdayName(targetDate);
      const dateString = this.getMonthName(targetDate);
      const dayOfMonth = this.getOrdinalDay(targetDate);
      const today = this.shopDate;
      const targetDateStr = this.formatDate(targetDate);
      const todayStr = this.formatDate(today);
      const isNotToday = targetDateStr !== todayStr;
      const isTomorrow = this.isTomorrow(targetDate, today);
      const shippingStrings = window.theme?.strings?.product?.shipping;
      let message = this.messageTemplate;
      let dayLabel = '';
      let hoursLabel = `${timeString}ч`;

      if (!isNotToday) {
        dayLabel = shippingStrings.today.replace('{{dayName}}', dayName);
      } else if (isTomorrow) {
        dayLabel = shippingStrings.tomorrow.replace('{{dayName}}', dayName);
      } else {
        const remainingDays = Math.ceil(timeString.split(':')[0] / 24);

        if (remainingDays >= ShippingMessage.MIN_DAYS_FOR_SPECIFIC_DATE) {
          dayLabel = shippingStrings.on_date
            .replace('{{ordinalDay}}', dayOfMonth)
            .replace('{{monthName}}', dateString);

          hoursLabel = shippingStrings.in_days.replace('{{count}}', remainingDays);
        } else {
          let preposition = '';

          if (this.languageIsoCode.toLocaleLowerCase() === 'bg') {
            preposition = this.getBgPreposition(dayName);
          }

          dayLabel = shippingStrings.on_weekday
            .replace('{{dayName}}', dayName)
            .replace('{{preposition}}', preposition);
        } 
      }
      
      message = message.replace('[hours]', hoursLabel);
      message = message.replace('[day]', dayLabel);

      return message;
    }

    isTomorrow(targetDate, baseDate) {
      const tomorrow = new Date(baseDate);
      tomorrow.setDate(baseDate.getDate() + 1);

      return this.formatDate(targetDate) === this.formatDate(tomorrow);
    }

    getDateFnsLocale(isoLocale) {
      if (!window.dateFns?.locale) {
        return null
      };

      const locales = Object.values(window.dateFns.locale);

      let match = locales.find(locale => locale.code === isoLocale);

      if (match) {
        return match;
      };

      const lang = isoLocale.split('-')[0];

      match = locales.find(locale => locale.code === lang);
      
      if (match) {
        return match;
      };

      return window.dateFns.locale.enUS;
    }

    formatWithLocale(date, formatString) {
      const locale = this.getDateFnsLocale(this.shopLocale);

      if (!window.dateFns) {
        return '';
      }

      return window.dateFns.format(date, formatString, { locale });
    }

    getWeekdayName(date) {
      return this.formatWithLocale(date, 'EEEE');
    }

    getMonthName(date) {
      return this.formatWithLocale(date, 'LLLL');
    }

    getOrdinalDay(date) {
      return this.formatWithLocale(date, 'do');
    }

    getBgPreposition(word) {
      return /^[вф]/i.test(word) ? 'във' : 'в';
    }
  }

  customElements.define('shipping-message', ShippingMessage);
}
